import { mdiClose, mdiFileDocument, mdiUpload } from '@mdi/js';
import { Icon } from '@mdi/react';
import LoadingButton from '@mui/lab/LoadingButton';
import { Dialog, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Alert from '@mui/material/Alert';
import { ChangeEvent, useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import { FormattedMessage, MessageDescriptor } from 'react-intl';
import { useMutation } from '@tanstack/react-query';
import { type OperationStatus, useNotificationManager } from '@qre-mailing/smart-table';

import { useFileUpdateApiCall } from '../../hooks/useFileUpdateApiCall';
import useQreId from '../../hooks/useQreId';
import { defineStyles } from '../../theme';
import { appToast } from '../../toast/appToast';
import { DialogInfo, DialogInfoType } from '../DialogInfo/DialogInfo';

const styles = defineStyles({
  root: {
    height: '100%',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  headerText: {
    display: 'flex',
    alignItems: 'center',
    gap: 2,
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: 3,
    pt: 2,
  },
  formRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: 1.5,
    mt: 2,
  },
  fileSelectSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: 1,
    p: 2,
    border: '1px dashed',
    borderColor: 'divider',
    borderRadius: 2,
    backgroundColor: 'action.hover',
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
      borderColor: 'primary.main',
      backgroundColor: 'action.selected',
    },
  },
  fileSelectButton: {
    alignSelf: 'flex-start',
    mt: 2,
  },
  fileName: {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    mt: 1,
    color: 'text.secondary',
    fontSize: '0.875rem',
  },
  errorAlert: {
    mt: 1,
  },
  actions: {
    px: 3,
    pb: 2,
    gap: 2,
  },
});

export type UpdateDialogProps = {
  open: boolean;
  onClose: () => void;
};

export function UpdateDialog({ open, onClose }: UpdateDialogProps) {
  const { qreId } = useQreId();
  const fileUpdateApiCall = useFileUpdateApiCall(qreId);
  const notificationManager = useNotificationManager();
  const [error, setError] = useState<React.ReactNode | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const { mutateAsync: uploadAsync, isPending } = useMutation<{ operationId?: string }, Error, { file: File }>({
    retry: false,
    mutationFn: async ({ file }: { file: File }) => {
      const result = await fileUpdateApiCall(file);
      return result;
    },
  });

  const { register, resetField, setValue, watch, handleSubmit } = useForm<{
    file: File | null;
  }>({
    defaultValues: { file: null },
  });

  const fileInput = register('file');
  const selectedFile = watch('file');

  const handleFileSelect = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const maybeFile = e.target?.files?.[0];
      const reader = new FileReader();

      if (maybeFile) {
        reader.onload = function (e) {
          const CSVBlob = new Blob([new Uint8Array(e.target?.result as ArrayBuffer)], { type: maybeFile.type });
          const CSVFile = new File([CSVBlob], maybeFile.name, { type: maybeFile.type });
          setValue('file', CSVFile, { shouldValidate: true });
          setError(null);
        };
        reader.readAsArrayBuffer(maybeFile);
      }
    },
    [setValue]
  );

  const handleClose = useCallback(() => {
    resetField('file');
    setError(null);
    setIsProcessing(false);
    onClose();
  }, [resetField, onClose]);

  const onSubmit = useCallback(
    async (data: { file: File | null }) => {
      setError(null);

      if (!data.file) {
        const errorMessage = <FormattedMessage id="upload.noFileSelected.text" defaultMessage="Please select a file" />;
        setError(errorMessage);
        return;
      }

      try {
        const response = await uploadAsync({ file: data.file });
        if (response?.operationId && notificationManager) {
          setIsProcessing(true);
          notificationManager.trackOperation({
            operationId: response.operationId,
            processingToast: <FormattedMessage id="upload.verifyingMessage.text" defaultMessage="Verifying file..." />,
            successToast: (status: OperationStatus) => {
              const updated = status.processed?.updated ?? 0;
              const blockedInCall = status.processed?.blockedInCall ?? 0;
              const blockedFinalResponseCode = status.processed?.blockedFinalResponseCode ?? 0;
              const totalBlocked = status.processed?.blocked ?? 0;
              const noChanges = status.processed?.noChanges ?? 0;
              const notExists = status.processed?.notExists ?? 0;
              const totalCount = status.totalCount ?? 0;
              return (
                <>
                  <strong>
                    <FormattedMessage
                      id="upload.updateSuccessMessage.text"
                      defaultMessage="Data updated successfully!"
                    />
                  </strong>
                  <br />
                  {totalCount > 0 && (
                    <>
                      <FormattedMessage
                        id="upload.rowsProcessed.text"
                        defaultMessage="{processed} rows processed"
                        values={{ processed: totalCount }}
                      />
                      <br />
                    </>
                  )}
                  {status?.processed && (
                    <>
                      <br />
                      <FormattedMessage
                        id="upload.updated.text"
                        defaultMessage="{updated} updated"
                        values={{ updated }}
                      />
                      <br />
                      <FormattedMessage
                        id="upload.noChanges.text"
                        defaultMessage="{noChanges} no changes"
                        values={{ noChanges }}
                      />
                      <br />
                      <FormattedMessage
                        id="upload.blocked.text"
                        defaultMessage="{blocked} blocked"
                        values={{ blocked: totalBlocked }}
                      />
                      {blockedInCall > 0 && (
                        <>
                          <br />
                          <span style={{ marginLeft: 16 }}>
                            <FormattedMessage
                              id="upload.blockedInCall.text"
                              defaultMessage="• {blockedInCall} in call"
                              values={{ blockedInCall }}
                            />
                          </span>
                        </>
                      )}
                      {blockedFinalResponseCode > 0 && (
                        <>
                          <br />
                          <span style={{ marginLeft: 16 }}>
                            <FormattedMessage
                              id="upload.blockedFinalResponseCode.text"
                              defaultMessage="• {blockedFinalResponseCode} final response code"
                              values={{ blockedFinalResponseCode }}
                            />
                          </span>
                        </>
                      )}
                      <br />
                      <FormattedMessage
                        id="upload.notExists.text"
                        defaultMessage="{notExists} not exists"
                        values={{ notExists }}
                      />
                    </>
                  )}
                </>
              );
            },
            onSuccess: handleClose,
            onVerified: () => {
              handleClose();
              appToast(
                <FormattedMessage id="updateDialog.info" defaultMessage="Operation is processing in the background" />,
                { type: 'info' }
              );
            },
            isWarning: (status: OperationStatus) => (status.processed?.blocked ?? 0) > 0,
          });
        } else {
          handleClose();
        }
      } catch (err) {
        setError(<FormattedMessage {...(err as MessageDescriptor)} />);
      }
    },
    [uploadAsync, notificationManager, handleClose]
  );

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth sx={styles.root}>
      <DialogTitle sx={styles.header}>
        <Box sx={styles.headerText}>
          <FormattedMessage id="upload.updateDialog.title" defaultMessage="Update Data" />
        </Box>
        <ButtonBase onClick={handleClose} disableRipple>
          <Icon path={mdiClose} size={1} />
        </ButtonBase>
      </DialogTitle>

      <DialogContent sx={styles.content}>
        <form id="update-form" onSubmit={handleSubmit(onSubmit)}>
          <Box sx={styles.formRow}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              <FormattedMessage
                id="upload.update.description"
                defaultMessage="Zde je možné aktualizovat již importované řádky. Pokud potřebujete přidat nová data pro nová ipsosId, zvolte prosím CSV import."
              />
            </Typography>

            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
              <FormattedMessage id="upload.fileSection.title" defaultMessage="CSV File" />
            </Typography>

            <Box sx={styles.fileSelectSection}>
              <DialogInfo type={DialogInfoType.UPDATE} />
              <Button
                variant="outlined"
                component="label"
                sx={styles.fileSelectButton}
                startIcon={<Icon path={mdiFileDocument} size={1} />}
              >
                <FormattedMessage id="upload.selectFile.button" defaultMessage="Select File" />
                <input {...fileInput} type={'file'} accept={'.xlsx,.csv'} hidden onChange={handleFileSelect} />
              </Button>
              {selectedFile && (
                <Typography sx={styles.fileName}>
                  <Icon path={mdiFileDocument} size={0.875} />
                  <FormattedMessage
                    id="upload.selectedFile.text"
                    defaultMessage="Selected: {fileName}"
                    values={{ fileName: selectedFile.name }}
                  />
                </Typography>
              )}
            </Box>
          </Box>
          {error && (
            <Alert severity="error" sx={styles.errorAlert}>
              {error}
            </Alert>
          )}
        </form>
      </DialogContent>

      <DialogActions sx={styles.actions}>
        <Button onClick={handleClose} variant="outlined">
          <FormattedMessage id="common.cancel.button" defaultMessage="Cancel" />
        </Button>
        <LoadingButton
          type="submit"
          form="update-form"
          variant="contained"
          loading={isPending || isProcessing}
          startIcon={<Icon path={mdiUpload} size={1} />}
        >
          <FormattedMessage id="upload.update.button" defaultMessage="Update" />
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
